// Записи протокола во вкладках «Ответственные» и «Контрагенты» и запись справочника из короткой
// формы поповера (027: FR-012 – FR-019; contracts/api.md; research, Р3, Р13). Список не хранится —
// сервер считает его по текущим пунктам протокола; внешнему — без псевдонимов и мест работы.
import { z } from '../kernel/zod.ts';
import { DateSchema, IdSchema } from '../kernel/common.ts';
import {
  CreateOrganizationBodySchema,
  CreatePersonBodySchema,
  SetEmailBodySchema,
} from '../api/directory.ts';
import { ExternalTypeSchema, OrganizationSideSchema } from '../directory/record.ts';
import { PartyMarkSchema, partyFields } from './item.ts';

/** Записей во вкладке — все записи и тексты протокола (ДЭЗ — сотни). */
export const PROTOCOL_PARTIES_MAX = 5000;

export const PartiesListQuerySchema = z.strictObject({ field: z.enum(partyFields) });

/** Состояние строки вкладки: действующая запись, черновик, в архиве или текст. */
export const protocolPartyStates = ['active', 'draft', 'archived', 'text'] as const;
export const ProtocolPartyStateSchema = z.enum(protocolPartyStates);
export type ProtocolPartyState = z.infer<typeof ProtocolPartyStateSchema>;

export const ProtocolPartySchema = z.object({
  /** `record:<номер>` или `text:<ключ имени>` — ключ быстрого фильтра и `parties.replace`. */
  key: z.string(),
  kind: z.enum(['person', 'organization']).nullable(),
  recordId: IdSchema.nullable(),
  name: z.string(),
  /** Прежние имена и написания — «Ранее: …»; внешнему — пусто. */
  aliases: z.array(z.string()),
  /** Основное место работы человека; внешнему — null. */
  workplace: z
    .object({ organization: z.string().nullable(), position: z.string().nullable() })
    .nullable(),
  side: OrganizationSideSchema.nullable(),
  externalType: ExternalTypeSchema.nullable(),
  state: ProtocolPartyStateSchema,
  mark: PartyMarkSchema.nullable(),
  /** Текущих пунктов протокола (без архивных) с этим ключом в поле. */
  items: z.number().int().min(0),
  /**
   * Ближайший конец срока открытых пунктов (не «Выполнено», не «Отменено») — как «Пункты»
   * «Справочника» (028: FR-021, FR-022); нет срока — null.
   */
  nearestDue: DateSchema.nullable(),
  /**
   * Текст, похожий ровно на одну действующую запись вида поля: имя или псевдоним начинается
   * с текста целым словом (028: FR-023). У записи и у непохожего текста — null.
   */
  like: z.object({ recordId: IdSchema, name: z.string() }).nullable(),
  /** 032 (FR-023): у человека «В отъезде до» — последний день; нет — без поля. */
  away: DateSchema.optional(),
});
export type ProtocolParty = z.infer<typeof ProtocolPartySchema>;

export const ProtocolPartiesResponseSchema = z.object({
  items: z.array(ProtocolPartySchema).max(PROTOCOL_PARTIES_MAX),
  /** Ревизия протокола, по которой посчитан список. */
  revision: z.number().int().min(1),
});
export type ProtocolPartiesResponse = z.infer<typeof ProtocolPartiesResponseSchema>;

/**
 * Короткая форма поповера (027: FR-019): человек — ФИО, организация места работы, должность,
 * почта; организация — название, своя или внешняя, тип. `itemId` — пункт, где создают (журнал).
 */
export const PartyCreateBodySchema = z.discriminatedUnion('kind', [
  CreatePersonBodySchema.extend({
    kind: z.literal('person'),
    email: SetEmailBodySchema.shape.address.optional(),
    itemId: IdSchema,
  }).strict(),
  CreateOrganizationBodySchema.extend({
    kind: z.literal('organization'),
    itemId: IdSchema,
  }).strict(),
]);
export type PartyCreateBody = z.output<typeof PartyCreateBodySchema>;

export const PartyCreatedSchema = z.object({
  record: z.object({
    id: IdSchema,
    kind: z.enum(['person', 'organization']),
    name: z.string(),
    state: z.enum(['confirmed', 'draft']),
  }),
  /** Пар «тот же?», которые справочник предложил после создания (принцип XVI). */
  pairs: z.number().int().min(0),
});
export type PartyCreated = z.infer<typeof PartyCreatedSchema>;
